import { getInitials } from "./index";

interface NotificationOptions {
    title: string;
    body: string;
    icon?: string;
    tag?: string;
    data?: any;
    requireInteraction?: boolean;
    silent?: boolean;
    onClick?: (data?: any) => void;
    sound?: string;
}

interface AvatarOptions {
    name: string;
    avatarUrl?: string;
    size?: number;
    bgColor?: string;
    textColor?: string;
}

class NotificationService {
    private permission: NotificationPermission = 'default';
    private clickHandlers: Map<string, (data?: any) => void> = new Map();
    private audio: HTMLAudioElement | null = null;
    private supported: boolean;

    constructor() {
        this.supported = 'Notification' in window;

        if (this.supported) {
            this.permission = Notification.permission;
        }
    }

    async requestPermission(): Promise<boolean> {
        if (!this.supported) {
            console.warn('Browser không hỗ trợ Web Notification API');
            return false;
        }

        if (this.permission === 'granted') {
            return true;
        }

        try {
            const permission = await Notification.requestPermission();
            this.permission = permission;
            return permission === 'granted';
        } catch (error) {
            console.warn('Lỗi khi request notification permission:', error);
            return false;
        }
    }

    private generateAvatarIcon(options: AvatarOptions): string {
        const { name, size = 128, bgColor, textColor = '#ffffff' } = options;

        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');

        if (!ctx) return '';

        ctx.fillStyle = '#100f6d';
        ctx.fillRect(0, 0, size, size);

        const initial = getInitials(name);
        ctx.fillStyle = textColor;
        ctx.font = `bold ${size / 2}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(initial, size / 2, size / 2);

        return canvas.toDataURL();
    }

    // private getInitials(name: string): string {
    //     const words = name.trim().split(/\s+/);
    //     if (words.length >= 2) {
    //         return (words[0][0] + words[0][1]).toUpperCase();
    //     }
    //     debugger
    //     return name.substring(0, 2).toUpperCase();
    // }

    private getColorFromName(name: string): string {
        const colors = [
            '#1abc9c', '#2ecc71', '#3498db', '#9b59b6', '#34495e',
            '#16a085', '#27ae60', '#2980b9', '#8e44ad', '#2c3e50',
            '#f39c12', '#e67e22', '#e74c3c', '#95a5a6', '#d35400'
        ];

        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }

        return colors[Math.abs(hash) % colors.length];
    }

    async showNotificationWithAvatar(
        avatarOptions: AvatarOptions,
        notificationOptions: Omit<NotificationOptions, 'icon'>
    ): Promise<Notification | null> {
        const hasPermission = await this.requestPermission();
        if (!hasPermission) return null;

        const icon = avatarOptions.avatarUrl ||
            this.generateAvatarIcon(avatarOptions);

        return this.showNotification({
            ...notificationOptions,
            icon
        });
    }

    private playNotificationSound(soundUrl?: string): void {
        try {
            const url = soundUrl || '/notification.mp3'; // Default sound

            if (this.audio) {
                this.audio.pause();
                this.audio.currentTime = 0;
            }

            this.audio = new Audio(url);
            this.audio.volume = 0.5;
            this.audio.play().catch(err => {
                console.warn('Không thể phát âm thanh notification:', err);
            });
        } catch (error) {
            console.warn('Lỗi khi phát âm thanh:', error);
        }
    }

    async showNotification(
        options: NotificationOptions
    ): Promise<Notification | null> {
        if (!this.supported) {
            console.warn('Browser không hỗ trợ notification, chỉ phát âm thanh');
            if (!options.silent && options.sound !== null) {
                this.playNotificationSound(options.sound);
            }
            return null;
        }

        const hasPermission = await this.requestPermission();
        if (!hasPermission) {
            console.warn('Không có quyền hiển thị notification');
            return null;
        }

        if (document.hasFocus()) {
            return null;
        }

        const {
            title,
            body,
            icon,
            tag,
            data,
            requireInteraction,
            silent,
            onClick,
            sound
        } = options;

        const notificationId = tag || `notif-${Date.now()}`;

        if (onClick) {
            this.clickHandlers.set(notificationId, onClick);
        }

        const serializableData = {
            ...data,
            _notificationId: notificationId,
        };

        try {
            const notification = new Notification(title, {
                body,
                icon,
                tag,
                data: serializableData,
                requireInteraction,
                silent,
                badge: icon,
            });

            if (!silent && sound !== null) {
                this.playNotificationSound(sound);
            }

            notification.onclick = (event) => {
                event.preventDefault();
                window.focus();

                const handler = this.clickHandlers.get(notificationId);
                if (handler) {
                    handler(data);
                    this.clickHandlers.delete(notificationId);
                }

                notification.close();
            };

            notification.onclose = () => {
                this.clickHandlers.delete(notificationId);
            };

            if (!requireInteraction) {
                setTimeout(() => {
                    notification.close();
                    this.clickHandlers.delete(notificationId);
                }, 5000);
            }

            return notification;
        } catch (error) {
            console.error('Lỗi khi tạo notification:', error);
            this.clickHandlers.delete(notificationId);
            return null;
        }
    }

    hasPermission(): boolean {
        return this.supported && this.permission === 'granted';
    }

    isSupported(): boolean {
        return this.supported;
    }

    cleanup(): void {
        this.clickHandlers.clear();
        if (this.audio) {
            this.audio.pause();
            this.audio = null;
        }
    }
}

export const notificationService = new NotificationService();

export const showMessageNotification = async (message: {
    sender_name: string;
    sender_avatar?: string;
    message_body: string;
    conversation_id?: number;
    onClick?: () => void;
}) => {
    await notificationService.showNotificationWithAvatar(
        {
            name: message.sender_name,
            avatarUrl: message.sender_avatar,
        },
        {
            title: message.sender_name,
            body: message.message_body,
            tag: `message-${message.conversation_id}`,
            data: {
                type: 'message',
                conversationId: message.conversation_id,
            },
            onClick: (data) => {
                if (message.onClick) message.onClick();
            },
            sound: '/sounds/message.mp3',
        }
    );
};

export const showSystemNotification = async (
    title: string,
    body: string,
    data?: any
) => {
    await notificationService.showNotification({
        title,
        body,
        icon: '/logo.png',
        data,
        onClick: () => {
            console.log('System notification clicked');
        }
    });
};

export const showNotificationWithBadge = async (
    title: string,
    body: string,
    count: number
) => {
    await notificationService.showNotification({
        title: `(${count}) ${title}`,
        body,
        icon: '/logo.png',
        tag: 'badge-notification',
    });
};

export const showGroupedMessageNotification = async (messages: Array<{
    sender_name: string;
    message_body: string;
}>) => {
    const count = messages.length;
    const firstMessage = messages[0];

    await notificationService.showNotification({
        title: `${count} tin nhắn mới`,
        body: `${firstMessage.sender_name}: ${firstMessage.message_body}`,
        icon: '/logo.png',
        tag: 'grouped-messages',
        onClick: () => {
            window.location.href = '/messages';
        }
    });
};